import {
  AfterViewInit,
  forwardRef,
  OnDestroy,
  OnInit,
  QueryList,
  ChangeDetectorRef,
  Component,
  ElementRef,
  ViewChildren,
  inject,
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';

import type { KeyboardEvent } from 'react';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { trackByValue } from '@core/common-legacy';
import { KeyboardEventCode } from '@core/common-legacy/keyboard';
import type {
  MentionElement,
  MentionElementGroup,
  MentionNavigationKey,
} from '@core/prosemirror-legacy';
import { MENTION_STRATEGY_TOKEN } from '@core/prosemirror-legacy';
import { injectMentionsFooterMessage } from '@core/prosemirror-legacy/angular';
import { DividerComponent, type TooltipPosition } from '@core/user-interface-legacy';
import {
  AvatarComponent,
  DropdownComponent,
  DropdownHeaderComponent,
  DropdownItemComponent,
  FormFieldComponent,
  IconComponent,
  InputComponent,
  RtTooltip,
  TruncateComponent,
  WindowRef,
} from '@core/user-interface-legacy';

import { GroupedMentionsListService } from './grouped-mentions-list.service';

@Component({
  selector: 'rt-grouped-mentions',
  templateUrl: './grouped-mentions.component.html',
  styleUrls: ['./grouped-mentions.component.less'],
  host: {
    '(keyup)': 'onKeyUp($event)',
    '(keydown)': 'onKeyDown($event)',
  },
  providers: [
    {
      provide: MENTION_STRATEGY_TOKEN,
      // eslint-disable-next-line @angular-eslint/no-forward-ref
      useClass: forwardRef(() => GroupedMentionsListService),
    },
  ],
  imports: [
    DropdownComponent,
    FormFieldComponent,
    InputComponent,
    ReactiveFormsModule,
    IconComponent,
    DropdownHeaderComponent,
    DropdownItemComponent,
    RtTooltip,
    AvatarComponent,
    TruncateComponent,
    DividerComponent,
  ],
})
export class GroupedMentionsComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly hostEl = inject(ElementRef);
  private readonly windowRef = inject(WindowRef);

  protected readonly footerMessage = injectMentionsFooterMessage({ optional: true }) ?? null;

  @ViewChildren('tooltipList') public tooltipList: QueryList<RtTooltip>;
  public highlightedIndex = 0;
  public selectedIndex: number | null = null;
  public searchFormControl = new UntypedFormControl('');
  public trackByValue = trackByValue;

  public searchEnabled: boolean;
  public queryChange: (val: string) => void;
  public select: (val: MentionElement) => void;
  public close: () => void;

  public groupedData: MentionElementGroup[];
  public elCount: number;
  public tooltipPosition: TooltipPosition = 'left';

  private ngOnDestroy$ = new Subject<void>();

  public ngOnInit() {
    this.searchFormControl.valueChanges.pipe(takeUntil(this.ngOnDestroy$)).subscribe(value => {
      this.highlightedIndex = 0;
      this.queryChange(value);
    });

    this.windowRef.resize$
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(() => this.setTooltipPosition());
  }

  public ngAfterViewInit() {
    this.setTooltipPosition();
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public onKeyUp(event: KeyboardEvent) {
    this.handleKey(<MentionNavigationKey>event.key);
  }

  public onKeyDown(event: KeyboardEvent) {
    if (KeyboardEventCode.Escape === event.key) {
      event.stopPropagation();
      this.close();
    }
  }

  public onElementHover(index: number, groupIndex: number) {
    this.highlightedIndex = this.computedIndex(index, groupIndex);
    this.cdRef.detectChanges();
    this.hideTooltip();
  }

  public computedIndex(index: number, groupIndex: number) {
    return groupIndex === 0
      ? index
      : this.groupedData.slice(0, groupIndex).reduce((acc, group) => acc + group.data.length, 0) +
          index;
  }

  /**
   * Based on currentIndex calculate current group index and index inside that group. Returns { index: -1, groupIndex: -1} if there are no non-empty groups
   * @param currentIndex - highlighted user's index in the dropdown
   */

  public extractComputedIndices(currentIndex: number): { index: number; groupIndex: number } {
    if (!this.groupedData.some(group => group.data.length > 0)) {
      return { index: -1, groupIndex: -1 };
    }

    let counter = 0;

    const groupIndex = this.groupedData.findIndex(group => {
      const groupLength = group?.data.length;
      const withinRange = counter + groupLength > currentIndex;
      if (!withinRange) counter += groupLength;
      return withinRange;
    });

    return {
      index: currentIndex - counter,
      groupIndex,
    };
  }

  public handleKey(key: MentionNavigationKey): void {
    if (KeyboardEventCode.Enter === key) {
      const extractedIndices = this.extractComputedIndices(this.highlightedIndex);
      const mentionGroup = this.groupedData[extractedIndices.groupIndex];

      if (!mentionGroup) {
        return;
      }

      const mention = mentionGroup.data[extractedIndices.index];
      if (mention) {
        if (!mentionGroup.disabled) {
          this.select(mention);
        } else {
          const tooltipRef = this.tooltipList.get(this.highlightedIndex);
          this.selectedIndex = this.selectedIndex !== null ? null : this.highlightedIndex;
          if (this.selectedIndex !== null) {
            tooltipRef!.hideSubject.next({ eventEmitted: true, delay: 0 });
            tooltipRef!.showSubject.next({ delay: 0 });
          } else {
            tooltipRef!.hideSubject.next({ eventEmitted: true, delay: 0 });
          }
        }
      } else {
        this.close();
      }
    } else if ([KeyboardEventCode.ArrowDown, KeyboardEventCode.ArrowUp].includes(key)) {
      this.hideTooltip();
      if (KeyboardEventCode.ArrowDown === key) {
        this.highlightedIndex = (this.highlightedIndex + 1) % this.elCount;
      } else if (KeyboardEventCode.ArrowUp === key) {
        this.highlightedIndex =
          this.highlightedIndex === 0
            ? this.elCount - 1
            : (this.highlightedIndex - 1) % this.elCount;
      }
      this.cdRef.detectChanges();
    }
  }

  public trackByMention(index: number, item: MentionElement) {
    return `${item.type}${item.id}`;
  }

  public handleClick(group: MentionElementGroup, el: MentionElement, event: MouseEvent) {
    event.stopPropagation();
    if (group.disabled) return;
    this.select(el);
  }

  private hideTooltip() {
    if (this.selectedIndex !== null) {
      const tooltipRef = this.tooltipList.get(this.selectedIndex);
      tooltipRef!.hideSubject.next({ eventEmitted: true, delay: 0 });
      this.selectedIndex = null;
    }
  }

  private setTooltipPosition(): void {
    const tooltipWidthPx = this.tooltipList.get(this.tooltipList.length - 1)?.maxWidth() ?? 300;
    const hostElRect = this.hostEl.nativeElement.getBoundingClientRect();
    const distFromLeftPx = hostElRect.left;
    const distFromRightPx = this.windowRef.nativeWindow.innerWidth - hostElRect.right;

    if (distFromLeftPx > tooltipWidthPx) {
      this.tooltipPosition = 'left';
    } else if (distFromRightPx > tooltipWidthPx) {
      this.tooltipPosition = 'right';
    }

    this.cdRef.detectChanges();
  }
}
